// src/contexts/JourneyMemoryContext.tsx
// Phase 0.5 spine — fixed loading state so it never hangs

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { JourneyStateService } from '@/services/JourneyStateService';
import { EventSystem } from '@/services/EventSystem';
import { MemoryGraphService } from '@/services/MemoryGraphService';
import { TaskEngine } from '@/services/TaskEngine';
import { DependencyEngine } from '@/services/DependencyEngine';
import {
  JourneyState, JourneyEvent, Task,
  JourneyEventType, JourneyEventSource, JourneyHealthScore,
} from '@/types/Journey.types';

interface JourneyMemoryContextValue {
  state: JourneyState | null;
  events: JourneyEvent[];
  tasks: Task[];
  loading: boolean;
  refresh: () => Promise<void>;
  updateState: (updates: Partial<JourneyState>) => Promise<void>;
  completeMilestone: (milestoneId: string) => Promise<void>;
  emitEvent: (opts: {
    eventType: JourneyEventType;
    payload: Record<string, any>;
    source: JourneyEventSource;
    relatedId?: string;
  }) => Promise<void>;
  getTimeline: () => Promise<JourneyEvent[]>;
  getGraph: () => Promise<{ nodes: any[]; edges: any[] }>;
  getHealth: () => Promise<JourneyHealthScore>;
  services: {
    journeyState: JourneyStateService | null;
    eventSystem: EventSystem | null;
    memoryGraph: MemoryGraphService | null;
    taskEngine: TaskEngine | null;
    dependencyEngine: DependencyEngine | null;
  };
}

const JourneyMemoryContext = createContext<JourneyMemoryContextValue | undefined>(undefined);

export const JourneyMemoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();

  const [state, setState] = useState<JourneyState | null>(null);
  const [events, setEvents] = useState<JourneyEvent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const [journeyStateService, setJourneyStateService] = useState<JourneyStateService | null>(null);
  const [eventSystem, setEventSystem] = useState<EventSystem | null>(null);
  const [memoryGraph, setMemoryGraph] = useState<MemoryGraphService | null>(null);
  const [taskEngine, setTaskEngine] = useState<TaskEngine | null>(null);
  const [dependencyEngine, setDependencyEngine] = useState<DependencyEngine | null>(null);

  const loadedRef = useRef(false);

  // ── If auth resolves with no user, stop loading immediately ──
  useEffect(() => {
    if (!authLoading && !user?.id) {
      setLoading(false);
    }
  }, [authLoading, user?.id]);

  // ── Initialise services when user is known ───────────────────
  useEffect(() => {
    if (user?.id) {
      setJourneyStateService(new JourneyStateService(user.id));
      setEventSystem(new EventSystem(user.id));
      setMemoryGraph(new MemoryGraphService(user.id));
      setTaskEngine(new TaskEngine(user.id));
      setDependencyEngine(new DependencyEngine(user.id));
      loadedRef.current = false;
    }
  }, [user?.id]);

  // ── Load data once services are ready ────────────────────────
  const loadData = async () => {
    if (!journeyStateService || !eventSystem || !taskEngine) return;
    if (authLoading) return;

    setLoading(true);
    try {
      const [stateData, eventsData, tasksData] = await Promise.allSettled([
        journeyStateService.getJourneyState(),
        eventSystem.getTimeline({ limit: 50 }),
        taskEngine.getTasks(),
      ]);

      setState(stateData.status === 'fulfilled' ? stateData.value : null);
      setEvents(eventsData.status === 'fulfilled' ? eventsData.value : []);
      setTasks(tasksData.status === 'fulfilled' ? tasksData.value : []);
    } catch (err) {
      console.error('JourneyMemoryContext.loadData error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (journeyStateService && eventSystem && taskEngine && !loadedRef.current && !authLoading) {
      loadedRef.current = true;
      loadData();
    }
  }, [journeyStateService, eventSystem, taskEngine, authLoading]);

  // ── Actions ───────────────────────────────────────────────────
  const refresh = async () => {
    loadedRef.current = false;
    await loadData();
  };

  const updateState = async (updates: Partial<JourneyState>) => {
    if (!journeyStateService) return;
    const newState = await journeyStateService.updateJourneyState(updates);
    if (newState) setState(newState);
  };

  const completeMilestone = async (milestoneId: string) => {
    if (!journeyStateService) return;
    const newState = await journeyStateService.updateMilestone(milestoneId, 'completed');
    if (newState) setState(newState);
  };

  const emitEvent = async (opts: {
    eventType: JourneyEventType;
    payload: Record<string, any>;
    source: JourneyEventSource;
    relatedId?: string;
  }) => {
    if (!eventSystem) return;
    await eventSystem.emit(opts);
    const updated = await eventSystem.getTimeline({ limit: 50 });
    setEvents(updated);
  };

  const getTimeline = async (): Promise<JourneyEvent[]> => {
    if (!eventSystem) return [];
    return eventSystem.getTimeline({ limit: 100 });
  };

  const getGraph = async () => {
    if (!memoryGraph) return { nodes: [], edges: [] };
    return memoryGraph.getRelationshipGraph();
  };

  const getHealth = async (): Promise<JourneyHealthScore> => {
    if (!journeyStateService) {
      return {
        overall: 0,
        components: { readiness: 0, trust: 0, milestones: 0, documents: 0, financial: 0 },
        insights: [],
        recommendations: [],
      };
    }
    return journeyStateService.getJourneyHealth();
  };

  return (
    <JourneyMemoryContext.Provider
      value={{
        state, events, tasks, loading,
        refresh, updateState, completeMilestone,
        emitEvent, getTimeline, getGraph, getHealth,
        services: {
          journeyState: journeyStateService,
          eventSystem,
          memoryGraph,
          taskEngine,
          dependencyEngine,
        },
      }}
    >
      {children}
    </JourneyMemoryContext.Provider>
  );
};

export const useJourneyMemory = (): JourneyMemoryContextValue => {
  const context = useContext(JourneyMemoryContext);
  if (!context) {
    throw new Error('useJourneyMemory must be used within a JourneyMemoryProvider');
  }
  return context;
};
